import { Spacing4, Spacing6 } from 'autogen/design-tokens/tokens';
import type { ProductListVariantsModel, VariantDisplayType } from 'autogen/swagger/all';
import styled, { css } from 'styled-components';
import SwatchRenderer from 'components/product/Variants/Swatches/SwatchRenderer';
import { useRef } from 'react';
import useHiddenItemsObserver from 'hooks/globals/useHiddenItemsObserver';
import { removeListStyling } from 'modules/helpers/mixins';
import type { SwatchSize } from 'components/product/Variants/Swatches/shared/types';
import { breakpoint } from 'variables';
import Variant from './Variant';
import { rem } from 'modules/helpers/style';

// #region Styles
const Nav = styled.nav`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: ${Spacing6};
    overflow: hidden;
`;

const colorSquareWidth = 16;
const colorSquareGap = 4;
const List = styled.ul<{ $displayType: VariantDisplayType; $size: SwatchSize }>`
    ${removeListStyling}
    display: flex;
    flex-wrap: wrap;
    overflow: hidden;
    gap: ${Spacing4};
    padding-right: 1px; // hidden item fix

    ${({ $displayType, $size }) =>
        $displayType === 'Color' || ($size === 'xs' && $displayType === 'Text')
            ? css`
                  max-height: 16px;
              `
            : css`
                  max-height: 19px;
              `}

    // Fix number floating right on mobile
    ${({ $displayType }) =>
        $displayType === 'Color' &&
        css`
            max-width: ${colorSquareWidth * 3 + colorSquareGap * 2 + 1}px;

            ${breakpoint.up(376)} {
                max-width: ${colorSquareWidth * 4 + colorSquareGap * 3 + 1}px;
            }

            ${breakpoint.up(429)} {
                max-width: 100%;
            }
        `}
`;

const ListItem = styled.li<{ $displayType: VariantDisplayType; $lastVisibleItem: number }>`
    max-width: 100%;
    overflow: hidden;

    ${({ $displayType }) =>
        $displayType === 'Text' &&
        css`
            flex-grow: 1;
        `}
`;

const StyledVariant = styled(Variant)`
    flex-shrink: 0;
    min-width: ${rem(20)}; // To stop re-rendering loop
    line-height: 0; // to truly align center
    overflow: visible;
`;
// #endregion Styles

export default function MoreVariants({
    variants,
    size = 'sm',
    ...restProps
}: {
    variants: ProductListVariantsModel;
    size?: SwatchSize;
}) {
    const ref = useRef<HTMLUListElement>(null);
    const hiddenItems = useHiddenItemsObserver(ref);
    const { totalAmount, variantList } = variants;
    const remainingAmount = totalAmount - (variantList.length - hiddenItems);
    const lastVisibleItem = variantList.length - hiddenItems;
    const { displayType } = variantList[0];

    return (
        <Nav {...restProps}>
            <List ref={ref} $displayType={displayType} $size={size}>
                {variantList.map((variant) => (
                    <ListItem key={variant.productId} $displayType={displayType} $lastVisibleItem={lastVisibleItem}>
                        <SwatchRenderer {...variant} size={size} />
                    </ListItem>
                ))}
            </List>
            {remainingAmount > 0 && <StyledVariant>+{remainingAmount}</StyledVariant>}
        </Nav>
    );
}
